import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Sparkles, Pencil, Check, X, Trash2, FileText, Ruler, Layers3, ChevronRight } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EstimateTierStack, type Tier } from "@/components/job-detail/EstimateTierStack";

interface JobEstimate {
  id: string;
  label: string | null;
  status: string;
  total_squares: number;
  customer_price: number;
  cost_subtotal: number;
  overhead_amount: number;
  profit_amount: number;
  material_rate: number;
  labor_rate: number;
  tear_off_cost: number;
  dump_fee: number;
  created_at: string;
  measurement_id: string | null;
  package_prices?: any;
  selected_package?: string | null;
}

interface LineItem {
  id: string;
  item_name: string;
  description: string | null;
  quantity: number;
  unit_type: string;
  material_cost: number;
  labor_cost: number;
  total_cost: number;
  category: string | null;
}

interface EstimatesTabProps {
  jobId: string;
  onMeasureJob?: () => void;
  measuring?: boolean;
}

export const EstimatesTab = ({ jobId, onMeasureJob, measuring = false }: EstimatesTabProps) => {
  const { orgId, user } = useAuth();
  const [estimates, setEstimates] = useState<JobEstimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEstimate, setSelectedEstimate] = useState<JobEstimate | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [generating, setGenerating] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingLineItem, setEditingLineItem] = useState<LineItem | null>(null);
  const [catalogItems, setCatalogItems] = useState<any[]>([]);
  const [measurementData, setMeasurementData] = useState<any | null>(null);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [manufacturer, setManufacturer] = useState<string>("GAF");
  const [addToTier, setAddToTier] = useState<Tier>("better");
  const navigate = useNavigate();

  useEffect(() => {
    loadEstimates();
    if (orgId) loadCatalogItems();

    const handleEstimateCreated = () => {
      console.log('🔄 Refreshing estimates...');
      loadEstimates();
    };

    window.addEventListener('estimate-created', handleEstimateCreated);
    return () => window.removeEventListener('estimate-created', handleEstimateCreated);
  }, [jobId, orgId]);

  useEffect(() => {
    if (selectedEstimate) {
      loadLineItems(selectedEstimate.id);
      // Load measurement data if available
      if (selectedEstimate.measurement_id) {
        loadMeasurementData(selectedEstimate.measurement_id);
      } else {
        setMeasurementData(null);
      }
    }
  }, [selectedEstimate]);

  const loadMeasurementData = async (measurementId: string) => {
    try {
      const { data, error } = await supabase
        .from("measurements_roof")
        .select("*")
        .eq("id", measurementId)
        .single();

      if (error) throw error;
      setMeasurementData(data);
    } catch (error) {
      console.error("Error loading measurement data:", error);
      setMeasurementData(null);
    }
  };

  const loadCatalogItems = async () => {
    if (!orgId) return;
    
    try {
      const { data, error } = await supabase
        .from("catalog_items")
        .select("*")
        .eq("org_id", orgId)
        .eq("is_active", true)
        .order("name", { ascending: true });

      if (error) throw error;
      setCatalogItems(data || []);
    } catch (error) {
      console.error("Error loading catalog:", error);
    }
  };

  const loadEstimates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("job_estimates")
        .select("*")
        .eq("job_id", jobId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setEstimates(data || []);
      if (data && data.length > 0 && !selectedEstimate) {
        setSelectedEstimate(data[0]);
      }
    } catch (error) {
      console.error("Error loading estimates:", error);
      toast.error("Failed to load estimates");
    } finally {
      setLoading(false);
    }
  };

  const loadLineItems = async (estimateId: string, _packageOverride?: string | null) => {
    try {
      const { data, error } = await supabase
        .from("estimate_line_items")
        .select("*")
        .eq("estimate_id", estimateId)
        .order("line_order", { ascending: true });
      if (error) throw error;
      setLineItems(data || []);
    } catch (error) {
      console.error("Error loading line items:", error);
      toast.error("Failed to load line items");
    }
  };

  const handleSelectPackage = async (tier: "good" | "better" | "best") => {
    if (!selectedEstimate?.id) return;
    const prices = (selectedEstimate as any)?.package_prices || {};
    const subtotal = Number(prices[tier] || 0);
    const overheadPct = 15;
    const profitPct = 20;
    const overheadAmt = subtotal * (overheadPct / 100);
    const profitAmt = (subtotal + overheadAmt) * (profitPct / 100);
    const customerPrice = subtotal + overheadAmt + profitAmt;

    await supabase
      .from("job_estimates")
      .update({
        selected_package: tier,
        cost_subtotal: subtotal,
        overhead_amount: overheadAmt,
        profit_amount: profitAmt,
        customer_price: customerPrice,
      })
      .eq("id", selectedEstimate.id);

    setSelectedEstimate({
      ...selectedEstimate,
      selected_package: tier,
      cost_subtotal: subtotal,
      overhead_amount: overheadAmt,
      profit_amount: profitAmt,
      customer_price: customerPrice,
    });
    await loadLineItems(selectedEstimate.id);
    loadEstimates();
    toast.success(`Switched to ${tier.charAt(0).toUpperCase() + tier.slice(1)} package`);
  };

  const handleAISuggest = async () => {
    if (!selectedEstimate || !selectedEstimate.measurement_id) {
      toast.error("No measurement data found for this estimate");
      return;
    }

    try {
      setGenerating(true);
      toast.info("Building Good / Better / Best packages from your measurement...");

      // Look up the active trade for this job (defaults to roofing)
      const { data: jobTrade } = await supabase
        .from("job_trades")
        .select("id, trade_code")
        .eq("job_id", jobId)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();

      const tradeId = jobTrade?.id;
      const tradeCode = jobTrade?.trade_code || "roofing";

      if (!tradeId) {
        toast.error("No trade found on this job");
        return;
      }

      // Wipe any previous auto-generated rows so the G/B/B stack is the source of truth
      await supabase
        .from("estimate_line_items")
        .delete()
        .eq("estimate_id", selectedEstimate.id);

      const { data, error } = await supabase.functions.invoke("bridgette-generate-estimate", {
        body: {
          measurement_id: selectedEstimate.measurement_id,
          job_id: jobId,
          trade_id: tradeId,
          trade_code: tradeCode,
          existing_estimate_id: selectedEstimate.id,
          manufacturer,
        },
      });

      if (error) throw error;

      toast.success("Good / Better / Best packages generated. Pick one above.");
      await loadEstimates();
      await loadLineItems(selectedEstimate.id);
    } catch (error: any) {
      console.error("Error generating G/B/B estimate:", error);
      toast.error(error.message || "Failed to generate packages");
    } finally {
      setGenerating(false);
    }
  };

  const handleAddLineItem = async (catalogItem: any) => {
    if (!selectedEstimate) return;

    try {
      const newItem = {
        estimate_id: selectedEstimate.id,
        catalog_item_id: catalogItem.id,
        item_name: catalogItem.name,
        description: catalogItem.description || "",
        quantity: 1,
        unit_type: catalogItem.unit_type || "each",
        material_cost: catalogItem.material_cost || 0,
        labor_cost: catalogItem.labor_cost || 0,
        total_cost: (catalogItem.material_cost || 0) + (catalogItem.labor_cost || 0),
        category: catalogItem.category,
        is_auto_generated: false,
        line_order: lineItems.length,
        package: addToTier,
        tier: addToTier,
      };

      const { error } = await supabase.from("estimate_line_items").insert(newItem);

      if (error) throw error;

      toast.success("Item added");
      loadLineItems(selectedEstimate.id);
      setShowAddDialog(false);
    } catch (error) {
      console.error("Error adding item:", error);
      toast.error("Failed to add item");
    }
  };

  const handleUpdateLineItem = async (id: string, updates: Partial<LineItem>) => {
    try {
      const { error } = await supabase
        .from("estimate_line_items")
        .update(updates)
        .eq("id", id);

      if (error) throw error;

      toast.success("Item updated");
      loadLineItems(selectedEstimate!.id);
    } catch (error) {
      console.error("Error updating item:", error);
      toast.error("Failed to update item");
    }
  };

  const handleDeleteLineItem = async (id: string) => {
    try {
      const { error } = await supabase.from("estimate_line_items").delete().eq("id", id);

      if (error) throw error;

      toast.success("Item deleted");
      loadLineItems(selectedEstimate!.id);
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    }
  };

  const handleStartEdit = (estimateId: string, currentLabel: string) => {
    setEditingId(estimateId);
    setEditLabel(currentLabel || "");
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;

    try {
      const { error } = await supabase
        .from("job_estimates")
        .update({ label: editLabel })
        .eq("id", editingId);

      if (error) throw error;

      toast.success("Estimate label updated");
      loadEstimates();
      setEditingId(null);
      setEditLabel("");
    } catch (error) {
      console.error("Error updating estimate:", error);
      toast.error("Failed to update estimate");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditLabel("");
  };

  const handleDeleteEstimate = async (estimateId: string) => {
    if (!confirm("Delete this estimate and all its line items?")) return;
    try {
      await supabase.from("estimate_line_items").delete().eq("estimate_id", estimateId);
      const { error } = await supabase.from("job_estimates").delete().eq("id", estimateId);
      if (error) throw error;
      toast.success("Estimate deleted");
      if (selectedEstimate?.id === estimateId) setSelectedEstimate(null);
      loadEstimates();
    } catch (e: any) {
      console.error(e);
      toast.error("Failed to delete estimate");
    }
  };

  const activeTier = ((selectedEstimate as any)?.selected_package || "better") as Tier;
  const activeTierItems = lineItems.filter((li: any) => (li.package || "better") === activeTier);
  const activeTierTotal = activeTierItems.reduce((sum: number, item: any) => sum + Number(item.total_cost || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading estimates...</div>
      </div>
    );
  }

  if (estimates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-center">
          <FileText className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
          <h3 className="text-lg font-semibold">No Estimates Yet</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Create your first estimate from a measurement
          </p>
        </div>
        {onMeasureJob && (
          <Button onClick={onMeasureJob} disabled={measuring}>
            {measuring ? "Measuring..." : "Measure Roof & Create Estimate"}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-5 xl:grid-cols-[280px_minmax(0,1fr)] items-start">
        <aside className="space-y-4 xl:sticky xl:top-4">
          <Card className="overflow-hidden border-border/70 bg-card shadow-sm">
            <CardContent className="p-0">
              <div className="border-b border-border/60 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                      Estimates
                    </div>
                    <div className="mt-1 text-lg font-semibold">{estimates.length} saved versions</div>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Layers3 className="h-5 w-5" />
                  </div>
                </div>
              </div>
              <div className="max-h-[calc(100vh-14rem)] space-y-2 overflow-y-auto p-3">
                {estimates.map((estimate) => {
                  const isActive = selectedEstimate?.id === estimate.id;
                  return (
                    <Card
                      key={estimate.id}
                      className={cn(
                        "group cursor-pointer border transition-all",
                        isActive
                          ? "border-primary bg-primary/5 shadow-md ring-1 ring-primary/20"
                          : "border-border/70 bg-background hover:border-primary/35 hover:bg-muted/20"
                      )}
                      onClick={() => setSelectedEstimate(estimate)}
                    >
                      <CardContent className="p-3.5">
                        {editingId === estimate.id ? (
                          <div className="flex items-center gap-1.5">
                            <Input
                              value={editLabel}
                              onChange={(e) => setEditLabel(e.target.value)}
                              className="h-8 text-sm"
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                            />
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 shrink-0" onClick={(e) => { e.stopPropagation(); handleSaveEdit(); }}>
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 shrink-0" onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start gap-3 min-w-0">
                              <div className={cn(
                                "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border",
                                isActive ? "border-primary/30 bg-primary/10 text-primary" : "border-border/60 bg-muted/30 text-muted-foreground"
                              )}>
                                <FileText className="h-4 w-4" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <h3 className="truncate text-sm font-semibold leading-tight">
                                      {estimate.label || "Untitled Estimate"}
                                    </h3>
                                    <p className="mt-1 text-[11px] text-muted-foreground">
                                      {new Date(estimate.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-0.5 shrink-0 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 p-0"
                                      onClick={(e) => { e.stopPropagation(); handleStartEdit(estimate.id, estimate.label || ""); }}
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                      onClick={(e) => { e.stopPropagation(); handleDeleteEstimate(estimate.id); }}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                </div>
                                <div className="mt-3 flex items-center justify-between gap-3 border-t border-border/60 pt-3">
                                  <Badge variant={estimate.status === 'final' ? 'default' : 'secondary'} className="h-5 text-[10px]">
                                    {estimate.status === 'final' ? 'Final' : 'Draft'}
                                  </Badge>
                                  <div className="text-right">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Estimate total</div>
                                    <div className="text-sm font-bold text-primary tabular-nums">
                                      ${estimate.customer_price.toLocaleString()}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </aside>

        {selectedEstimate && (
          <div className="min-w-0 space-y-5">
            <Card className="overflow-hidden border-border/70 bg-card shadow-sm">
              <CardContent className="p-6">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Badge variant={selectedEstimate.status === 'final' ? 'default' : 'secondary'}>
                    {selectedEstimate.status === 'final' ? 'Final' : 'Draft'}
                  </Badge>
                  <Badge variant="outline" className="text-[11px]">
                    Created {new Date(selectedEstimate.created_at).toLocaleDateString()}
                  </Badge>
                </div>
                <h2 className="text-3xl font-bold tracking-tight">
                  {selectedEstimate.label || "Untitled Estimate"}
                </h2>
                <p className="mt-2 max-w-4xl text-sm text-muted-foreground">
                  Build a manufacturer-specific estimate, compare Good / Better / Best side by side, edit any tier, and push the selected tier directly into the proposal.
                </p>

                <div className="mt-5 grid gap-3 md:grid-cols-3">
                  <div className="rounded-lg border border-border/70 bg-muted/20 p-4">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Active tier</div>
                    <div className="mt-2 text-xl font-semibold capitalize">{activeTier}</div>
                  </div>
                  <div className="rounded-lg border border-border/70 bg-muted/20 p-4">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Items in tier</div>
                    <div className="mt-2 text-xl font-semibold tabular-nums">{activeTierItems.length}</div>
                  </div>
                  <div className="rounded-lg border border-border/70 bg-muted/20 p-4">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Tier total</div>
                    <div className="mt-2 text-xl font-semibold tabular-nums">${activeTierTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                  </div>
                </div>

                <div className="mt-5 rounded-lg border border-border/70 bg-card p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Ruler className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">Measurement snapshot</div>
                      <div className="text-xs text-muted-foreground">Reference values kept in the main workspace</div>
                    </div>
                  </div>

                  {measurementData ? (
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-lg border border-border/70 bg-card px-3 py-3">
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Squares</div>
                        <div className="mt-1 text-2xl font-bold tabular-nums">
                          {measurementData.total_squares ? Number(measurementData.total_squares).toFixed(1) : '—'}
                        </div>
                      </div>
                      {[
                        ['Area', measurementData.total_area ? `${Number(measurementData.total_area).toFixed(0)} sq ft` : '—'],
                        ['Pitch', measurementData.pitch || measurementData.raw_response?.parsed_data?.predominant_pitch || '—'],
                        ['Waste', measurementData.waste_factor ? `${Number(measurementData.waste_factor).toFixed(0)}%` : '—'],
                        ['Facets', measurementData.raw_response?.parsed_data?.number_of_facets || '—'],
                        ['Ridges', measurementData.ridges ? `${Number(measurementData.ridges).toFixed(1)} ft` : '—'],
                        ['Hips', measurementData.hips ? `${Number(measurementData.hips).toFixed(1)} ft` : '—'],
                        ['Valleys', measurementData.valleys ? `${Number(measurementData.valleys).toFixed(1)} ft` : '—'],
                        ['Eaves', measurementData.eaves ? `${Number(measurementData.eaves).toFixed(1)} ft` : '—'],
                        ['Rakes', measurementData.rakes ? `${Number(measurementData.rakes).toFixed(1)} ft` : '—'],
                      ].map(([label, val]) => (
                        <div key={label as string} className="rounded-lg border border-border/60 bg-background px-3 py-3">
                          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label as string}</div>
                          <div className="mt-1 font-mono text-base tabular-nums">{String(val)}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-border/70 bg-background px-4 py-6 text-sm text-muted-foreground">
                      No linked measurement data on this estimate yet.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/70 bg-card shadow-sm">
              <CardContent className="p-5">
                <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
                  <div className="min-w-0">
                    <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold">
                      Manufacturer / System
                    </Label>
                    <Select value={manufacturer} onValueChange={setManufacturer}>
                      <SelectTrigger className="mt-2 h-12 bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GAF">GAF</SelectItem>
                        <SelectItem value="CertainTeed">CertainTeed</SelectItem>
                        <SelectItem value="Owens Corning">Owens Corning</SelectItem>
                        <SelectItem value="Generic">Generic</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Pull Good / Better / Best systems from Catalog package systems for the selected manufacturer.
                    </p>
                  </div>
                  <Button
                    onClick={handleAISuggest}
                    disabled={generating}
                    size="lg"
                    className="h-12 min-w-[220px] shadow-md"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {generating ? "Generating..." : "Generate Good / Better / Best"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {lineItems.length === 0 ? (
              <Card className="border-dashed border-border/70 bg-muted/10 shadow-sm">
                <CardContent className="py-16 text-center text-muted-foreground">
                  <Sparkles className="mx-auto mb-3 h-8 w-8 opacity-40" />
                  <p className="font-medium text-foreground">No tiers generated yet</p>
                  <p className="mt-1 text-sm">Choose a manufacturer above and generate the full estimate stack.</p>
                </CardContent>
              </Card>
            ) : (
              <EstimateTierStack
                lineItems={lineItems as any}
                selectedTier={activeTier}
                packagePrices={(selectedEstimate as any)?.package_prices}
                onSelectTier={handleSelectPackage}
                onEditItem={(item) => setEditingLineItem(item as any)}
                onDeleteItem={handleDeleteLineItem}
                onAddItemToTier={(tier) => { setAddToTier(tier); setShowAddDialog(true); }}
                disabled={generating}
              />
            )}

            {selectedEstimate && lineItems.length > 0 && (
              <Card className="sticky bottom-4 border-primary/25 bg-card/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/85">
                <CardContent className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">
                      Ready to send
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-base font-semibold">
                      <span className="capitalize">{activeTier} tier</span>
                      <span className="text-muted-foreground">•</span>
                      <span>{activeTierItems.length} items</span>
                      <span className="text-muted-foreground">•</span>
                      <span className="tabular-nums">${activeTierTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      navigate(`/jobs/${jobId}?tab=Proposals&createProposal=true&estimateId=${selectedEstimate.id}&tier=${activeTier}`);
                    }}
                    size="lg"
                    className="min-w-[220px] shadow-md"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Send to Proposal
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Add Item Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) setCatalogSearch("");
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Item from Catalog</DialogTitle>
            <DialogDescription>Select an item to add to this estimate</DialogDescription>
          </DialogHeader>
          <div className="mb-4">
            <Input
              placeholder="Search catalog items..."
              value={catalogSearch}
              onChange={(e) => setCatalogSearch(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="max-h-96 overflow-y-auto">
            <div className="space-y-2">
              {catalogItems
                .filter((item) => 
                  item.name.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                  item.category?.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                  item.description?.toLowerCase().includes(catalogSearch.toLowerCase())
                )
                .map((item) => (
                <Card
                  key={item.id}
                  className="p-3 cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => handleAddLineItem(item)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium">{item.name}</p>
                      {item.category && (
                        <p className="text-xs text-muted-foreground">{item.category}</p>
                      )}
                      <div className="flex gap-2 mt-1">
                        {item.unit_price && (
                          <p className="text-xs font-semibold">
                            ${item.unit_price}/{item.unit_type || 'ea'}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))}
              {catalogItems.filter((item) => 
                item.name.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                item.category?.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                item.description?.toLowerCase().includes(catalogSearch.toLowerCase())
              ).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {catalogSearch ? "No items match your search" : "No catalog items found"}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Line Item Dialog */}
      {editingLineItem && (
        <Dialog open={!!editingLineItem} onOpenChange={() => setEditingLineItem(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Line Item</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Item Name</Label>
                <Input
                  value={editingLineItem.item_name}
                  onChange={(e) =>
                    setEditingLineItem({ ...editingLineItem, item_name: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingLineItem.description || ""}
                  onChange={(e) =>
                    setEditingLineItem({ ...editingLineItem, description: e.target.value })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    value={editingLineItem.quantity}
                    onChange={(e) =>
                      setEditingLineItem({
                        ...editingLineItem,
                        quantity: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Unit Type</Label>
                  <Select
                    value={editingLineItem.unit_type}
                    onValueChange={(value) =>
                      setEditingLineItem({ ...editingLineItem, unit_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="each">Each</SelectItem>
                      <SelectItem value="sqft">Sqft</SelectItem>
                      <SelectItem value="sq">Square</SelectItem>
                      <SelectItem value="lf">LF</SelectItem>
                      <SelectItem value="hour">Hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Material Cost</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingLineItem.material_cost}
                    onChange={(e) =>
                      setEditingLineItem({
                        ...editingLineItem,
                        material_cost: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Labor Cost</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingLineItem.labor_cost}
                    onChange={(e) =>
                      setEditingLineItem({
                        ...editingLineItem,
                        labor_cost: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingLineItem(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const quantity = editingLineItem.quantity || 0;
                  const materialCost = editingLineItem.material_cost || 0;
                  const laborCost = editingLineItem.labor_cost || 0;
                  const totalCost = quantity * (materialCost + laborCost);

                  handleUpdateLineItem(editingLineItem.id, {
                    ...editingLineItem,
                    total_cost: totalCost,
                  });
                  setEditingLineItem(null);
                }}
              >
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
